import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';
import { scroller } from 'react-scroll';
import { Menu, X, Globe } from 'lucide-react';

const Navbar = ({ logoSize = '2rem', customLinks }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, changeLanguage, translations } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate("/");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scroller.scrollTo(sectionId, { smooth: true, duration: 500 }), 100);
    } else {
      scroller.scrollTo(sectionId, { smooth: true, duration: 500 });
    }
  };

  const navLinks = customLinks || [
    { to: "/", label: translations[language]?.navbar?.title || 'Inicio' },
    { to: "/que-hacemos", label: translations[language]?.navbar?.option2 || '¿Qué hacemos?' },
    { to: "/noticias", label: translations[language]?.navbar?.news || 'Noticias' },
    { to: "/geoportal", label: translations[language]?.navbar?.geoportal || 'Geoportal' },
    { to: "/contactos", label: translations[language]?.navbar?.contacts || 'Contactos' },
  ];

  const linkClass = (isActive) => `
    px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors duration-200
    ${isActive ? 'text-green-600' : 'text-gray-700'}
  `;

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className={`bg-white shadow-md border-b border-gray-200 transition-shadow duration-300 ${
        isScrolled ? 'shadow-lg' : ''
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            
            {/* Logo */}
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <img src="/list_colab/ata_logo_blanco.png" alt="Logo" className="h-8 w-auto" />
              </div>
              <div className="hidden md:block">
                <p className="font-bold text-sm leading-tight m-0">
                  <span className="text-gray-900">VLIR-UOS</span>
                  <span className="text-gray-600 text-xs"> S.I</span>
                  <br />
                  <span className="text-base font-black tracking-wider bg-gradient-to-r">
                    SMART FARMING
                  </span>
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <ul className="hidden lg:flex items-center space-x-1">
              {navLinks.map(({ to, label }) => {
                const isActive = location.pathname === to;
                return to === "#sobre-el-proyecto" ? (
                  <li key={to}>
                    <button onClick={() => handleScrollToSection('sobre-el-proyecto')} className={linkClass(false)}>
                      {label}
                    </button>
                  </li>
                ) : (
                  <li key={to}>
                    <Link to={to} className={linkClass(isActive)}>
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center space-x-3">
              {/* Language Selector */}
              <div className="hidden sm:flex items-center space-x-2 bg-gray-100 rounded-lg px-2 py-1.5">
                <Globe className="text-gray-600 w-4 h-4" />
                {['es', 'en'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                      language === lang
                        ? 'bg-green-600 text-white'
                        : 'bg-transparent text-gray-600'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-700 bg-gray-100"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[600px] opacity-100 pb-6' : 'max-h-0 opacity-0'
          }`}>
            <div className="border-t border-gray-200 pt-4">
              <ul className="space-y-1">
                {navLinks.map(({ to, label }) => {
                  const isActive = location.pathname === to;
                  return (
                    <li key={to}>
                      {to === "#sobre-el-proyecto" ? (
                        <button
                          onClick={() => handleScrollToSection('sobre-el-proyecto')}
                          className="block w-full text-left px-4 py-3 text-gray-700 font-semibold"
                        >
                          {label}
                        </button>
                      ) : (
                        <Link
                          to={to}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-4 py-3 font-semibold ${
                            isActive ? 'text-green-600' : 'text-gray-700'
                          }`}
                        >
                          {label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col items-center space-y-4">
                <div className="flex items-center space-x-2 text-gray-700">
                  <Globe className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase tracking-wider">Idioma</span>
                </div>
                <div className="flex space-x-3">
                  {['es', 'en'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => changeLanguage(lang)}
                      className={`px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                        language === lang
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Navbar;
